import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface Course {
  id: string;
  codigo: string;
  titulo: string;
  descripcion: string;
  duracion: string;
  modalidad: string;
  precio: number;
  imagen_url: string;
  categoria: string;
  nivel: string;
}

export default function Courses() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      const { data, error } = await supabase
        .from('cursos')
        .select('*')
        .eq('activo', true)
        .order('orden', { ascending: true })
        .limit(4);

      if (error) throw error;
      setCourses(data || []);
    } catch (error) {
      console.error('Error al cargar cursos:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRDText = (codigo: string) => {
    switch(codigo) {
      case 'ECP1741_2':
        return 'RD 545/2023';
      case 'ECP1742_2':
      case 'ECP1743_2':
      case 'ECP1744_2':
        return 'RD 917/2024';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500"></div>
          <p className="mt-4 text-gray-600">Cargando cursos...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 md:mb-4">
            Programa Formativo Completo
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Nuestra Aula Virtual te ofrece este programa de formación profesional que está diseñado para darte una Formación de calidad con reconocimiento oficial y certificación de aprovechamiento.
          </p>
        </div>

        {courses.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No hay cursos disponibles en este momento.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mb-8 md:mb-12">
              {courses.map((course) => (
                <article
                  key={course.id}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
                >
                  <div className="relative h-48 sm:h-56 overflow-hidden">
                    <img
                      src={course.imagen_url}
                      alt={course.titulo}
                      className="w-full h-full object-contain object-center bg-gray-50"
                    />
                    <div className="absolute top-3 left-3 md:top-4 md:left-4 bg-primary-500 text-white px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-semibold">
                      {course.codigo}
                    </div>
                    <div className="absolute bottom-3 left-3 md:bottom-4 md:left-4 bg-white/95 backdrop-blur-sm px-3 py-1.5 md:px-4 md:py-2 rounded-full flex items-center gap-2">
                      <i className="ri-shield-check-fill text-secondary-600 text-sm md:text-base"></i>
                      <span className="text-xs md:text-sm font-semibold text-gray-900">Certificación Oficial</span>
                    </div>
                  </div>
                  <div className="p-5 md:p-8">
                    <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-2 md:mb-3">
                      {course.titulo}
                    </h3>
                    <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6 leading-relaxed">
                      {course.descripcion}
                    </p>
                    <div className="mb-4 md:mb-6 flex items-center gap-3 flex-wrap">
                      {getRDText(course.codigo) && (
                        <span className="inline-flex items-center gap-1 bg-primary-50 text-primary-700 px-3 py-1 rounded-full text-xs md:text-sm font-medium">
                          <i className="ri-file-text-line"></i>
                          {getRDText(course.codigo)}
                        </span>
                      )}
                    </div>
                    <Link
                      to="/cursos"
                      className="inline-flex items-center text-accent-500 font-semibold hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap text-sm md:text-base"
                    >
                      Ver detalles del curso
                      <i className="ri-arrow-right-line ml-2"></i>
                    </Link>
                  </div>
                </article>
              ))}
            </div>

            <div className="text-center">
              <Link
                to="/cursos"
                className="inline-flex items-center justify-center rounded-full bg-accent-500 px-6 md:px-8 py-3 md:py-4 text-sm md:text-base font-semibold text-white shadow-lg hover:bg-accent-600 transition-all hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Ver Todos los Cursos
                <i className="ri-arrow-right-line ml-2 text-lg md:text-xl"></i>
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
